'use client'

import type { ImageGalleryFieldsFragment } from '@/graphql/types'
import { DynamicImageGallery } from '@ccpgames/eve-web-core'
import Image from 'next/image'
import Lightbox from 'yet-another-react-lightbox'
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails'
import Download from 'yet-another-react-lightbox/plugins/download'
import 'yet-another-react-lightbox/styles.css'
import 'yet-another-react-lightbox/plugins/thumbnails.css'
import NextJsImage from '@/components/shared/image-gallery/NextJsImage'
import { useContentfulEntryLivePreview } from '@/components/providers/useContentfulEntryLivePreview'
import lightboxClasses from './ImageGalleryLightbox.module.css'

interface ImageGalleryProps {
  entry: ImageGalleryFieldsFragment
}

const thumbnailSizes = '(min-width: 1200px) 25vw, (min-width: 768px) 33vw, (min-width: 480px) 50vw, 100vw'
const thumbnailImageWidth = 600

export function ImageGallery({ entry }: ImageGalleryProps) {
  const { updatedEntry, inspectorProps } = useContentfulEntryLivePreview(entry)
  const title = updatedEntry.title ?? undefined
  const description = updatedEntry.description ?? undefined
  const columns = updatedEntry.columnCount ?? undefined
  const downloadable = updatedEntry.downloadable ?? false

  const images =
    updatedEntry.imagesCollection?.items
      ?.filter((image): image is NonNullable<typeof image> => Boolean(image?.url))
      .map((image, index) => {
        const baseUrl = image.url as string

        return {
          id: `${image.sys.id}-${index}`,
          // Full-size for lightbox
          src: `${baseUrl}?fm=jpg&w=2400&q=75`,
          thumbnailSrc: `${baseUrl}?fm=webp&w=${thumbnailImageWidth}&q=75`,
          alt: image.description ?? undefined,
          caption: image.title ?? undefined,
          width: image.width ?? undefined,
          height: image.height ?? undefined,
        }
      }) ?? []

  return (
    <div {...inspectorProps('imagesCollection')}>
      <DynamicImageGallery
        title={title}
        description={description}
        images={images}
        columns={columns}
        renderThumbnail={({ image, open, className, ariaLabel }) => (
          <button type="button" className={className} onClick={open} aria-label={ariaLabel}>
            <Image
              src={image.thumbnailSrc ?? image.src}
              alt={image.alt ?? ''}
              fill
              sizes={thumbnailSizes}
              style={{ objectFit: 'cover' }}
            />
          </button>
        )}
        renderLightbox={({ open, close, index, slides }) => (
          <Lightbox
            open={open}
            close={close}
            index={index}
            className={lightboxClasses.lightboxOverride}
            slides={slides.map((s, slideIndex) => ({
              src: s.src,
              width: s.width,
              height: s.height,
              alt: s.alt,
              caption: s.caption,
              thumbnail: images[slideIndex]?.thumbnailSrc,
              ...(downloadable
                ? {
                    downloadFilename: s.caption ? `${s.caption}.jpg` : `image-${slideIndex + 1}.jpg`,
                  }
                : {}),
            }))}
            thumbnails={{
              imageFit: 'cover',
            }}
            plugins={downloadable ? [Thumbnails, Download] : [Thumbnails]}
            render={{
              slide: NextJsImage,
            }}
          />
        )}
      />
    </div>
  )
}
